import React from 'react';
import { Helmet } from 'react-helmet-async';
import { MapPin, Heart, Dumbbell, Globe, Sparkles, Twitter, Instagram } from 'lucide-react';

const MyStory = () => {
  return (
    <>
      <Helmet>
        <title>My Story - Daily Wit | Meet David</title>
        <meta name="description" content="Meet David, the founder of Daily Wit. A world traveler, energy healer, and the guy who got tired of boring headlines." />
      </Helmet>
      
      <div className="min-h-screen bg-[#FFF8E7] dark:bg-gray-900 py-12 px-4">
        <div className="max-w-3xl mx-auto">
          
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-5xl md:text-6xl font-bold mb-4 text-gray-900 dark:text-white" style={{ fontFamily: 'Merriweather, serif' }}>
              My Story
            </h1>
            <div className="flex items-center justify-center gap-2 text-gray-600 dark:text-gray-400">
              <MapPin className="w-5 h-5" />
              <span>40+ countries and counting</span>
            </div>
          </div>

          {/* Main Photo - PSG Locker Room */}
          <div className="mb-12 rounded-xl overflow-hidden border-2 border-black dark:border-gray-700 hard-shadow-lg">
            <img 
              src="/images/story/hero.jpg" 
              alt="David in PSG locker room"
              className="w-full h-auto object-cover"
            />
          </div>

          {/* Content */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border-2 border-black dark:border-gray-700 hard-shadow-lg p-8 md:p-12">
            
            {/* Intro */}
            <p className="text-xl md:text-2xl text-gray-800 dark:text-gray-200 leading-relaxed mb-8">
              Hey, I'm <span className="font-bold text-[#FF6B6B]">David</span> — the guy behind Daily Wit.
            </p>

            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-6">
              I've spent most of my adult life moving around: Europe, the Middle East, South America, the Caribbean… 
              I've worked and lived in so many places I've lost count, and I've travelled to over <strong>40 countries</strong>.
            </p>

            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-8">
              The best part? <strong>The people.</strong> I've made friends and colleagues everywhere — from Canada and the US 
              to France, New Zealand, Mongolia, Chile… you name it. I still talk regularly with folks across time zones, 
              and yes, the group chat memes at 3 a.m. still get me in trouble 😅
            </p>

            {/* Photo Grid - Travel Photos */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 my-10">
              <div className="aspect-square rounded-lg border-2 border-black overflow-hidden">
                <img 
                  src="/images/story/family.jpg" 
                  alt="Family at coastal town"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="aspect-square rounded-lg border-2 border-black overflow-hidden">
                <img 
                  src="/images/story/bicycle.jpg" 
                  alt="Giant bicycle sculpture in Pyrenees"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="aspect-square rounded-lg border-2 border-black overflow-hidden">
                <img 
                  src="/images/story/drumming.jpg" 
                  alt="Drumming with community in Africa"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            {/* Amazing Teams Section */}
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6" style={{ fontFamily: 'Merriweather, serif' }}>
              I've worked with amazing teams from all over:
            </h2>

            <div className="space-y-4 mb-10">
              <div className="flex items-start gap-3 p-4 bg-[#FFF8E7] dark:bg-gray-700 rounded-lg">
                <span className="text-2xl">🇵🇭</span>
                <p className="text-gray-700 dark:text-gray-300">
                  <strong>Filipinos</strong> who can make you laugh until your stomach hurts (and then feed you the best food)
                </p>
              </div>
              <div className="flex items-start gap-3 p-4 bg-[#FFF8E7] dark:bg-gray-700 rounded-lg">
                <span className="text-2xl">🇨🇱</span>
                <p className="text-gray-700 dark:text-gray-300">
                  <strong>Chileans</strong> who give everyone a nickname in 5 minutes — instant family vibes
                </p>
              </div>
              <div className="flex items-start gap-3 p-4 bg-[#FFF8E7] dark:bg-gray-700 rounded-lg">
                <span className="text-2xl">🇵🇱</span>
                <p className="text-gray-700 dark:text-gray-300">
                  <strong>Polish friends</strong> who work hard all day and then throw legendary parties
                </p>
              </div>
              <div className="flex items-start gap-3 p-4 bg-[#FFF8E7] dark:bg-gray-700 rounded-lg">
                <span className="text-2xl">🇮🇳</span>
                <p className="text-gray-700 dark:text-gray-300">
                  <strong>Indians</strong> who bring incredible dedication and real kindness every single day
                </p>
              </div>
              <div className="flex items-start gap-3 p-4 bg-[#FFF8E7] dark:bg-gray-700 rounded-lg">
                <span className="text-2xl">🌴</span>
                <p className="text-gray-700 dark:text-gray-300">
                  <strong>Caribbean people</strong> who keep smiling and spreading warmth no matter what life throws at them
                </p>
              </div>
            </div>

            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-10">
              And that's just a few — I've been lucky to meet great humans from everywhere, including tons of awesome 
              Americans, Canadians, French, Aussies, and more who are still part of my daily life.
            </p>

            {/* Passions Section */}
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6" style={{ fontFamily: 'Merriweather, serif' }}>
              I'm passionate about:
            </h2>

            <div className="grid md:grid-cols-3 gap-4 mb-10">
              <div className="text-center p-6 bg-gradient-to-br from-[#FF6B6B]/10 to-[#FF6B6B]/5 rounded-xl border-2 border-[#FF6B6B]/30">
                <Globe className="w-10 h-10 text-[#FF6B6B] mx-auto mb-3" />
                <h3 className="font-bold text-gray-900 dark:text-white mb-2">Geopolitics</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">The world is wild and I love following the chess game</p>
              </div>
              <div className="text-center p-6 bg-gradient-to-br from-[#4ECDC4]/10 to-[#4ECDC4]/5 rounded-xl border-2 border-[#4ECDC4]/30">
                <Heart className="w-10 h-10 text-[#4ECDC4] mx-auto mb-3" />
                <h3 className="font-bold text-gray-900 dark:text-white mb-2">Nature</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">Mountains, ocean, forest — always better than concrete</p>
              </div>
              <div className="text-center p-6 bg-gradient-to-br from-[#667eea]/10 to-[#667eea]/5 rounded-xl border-2 border-[#667eea]/30">
                <Sparkles className="w-10 h-10 text-[#667eea] mx-auto mb-3" />
                <h3 className="font-bold text-gray-900 dark:text-white mb-2">Wellness & Energy</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">Yes, I'm that guy talking chakras at the barbecue 🧘</p>
              </div>
            </div>

            {/* Sports & Fitness */}
            <div className="flex items-center gap-4 p-6 bg-[#FFE66D]/20 rounded-xl border-2 border-[#FFE66D] mb-10">
              <Dumbbell className="w-12 h-12 text-[#FF6B6B]" />
              <div>
                <p className="text-gray-800 dark:text-gray-200 leading-relaxed">
                  <strong>Sports are still a big part of my life:</strong> I play basketball, tennis, and stay consistent with fitness. 
                  I just turned <strong>45 in January</strong> and my goal is simple — be fitter at 50 than I was at 25 💪
                </p>
              </div>
            </div>

            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-8">
              I also love bugging my wife and friends (they pretend they hate it… but we all know the truth 😂)
            </p>

            {/* Why Daily Wit */}
            <div className="bg-gradient-to-r from-[#FF6B6B] to-[#4ECDC4] p-1 rounded-xl mb-10">
              <div className="bg-white dark:bg-gray-800 rounded-lg p-6">
                <p className="text-lg text-gray-800 dark:text-gray-200 leading-relaxed">
                  <strong className="text-[#FF6B6B]">Daily Wit</strong> started because I got tired of dry, boring headlines every morning. 
                  So I decided to remix them with <strong>sarcasm, a little roast, and zero pretension</strong>. 
                  Hope you enjoy the daily twist — and if a line ever makes you laugh out loud, <em>mission accomplished</em>.
                </p>
              </div>
            </div>

            {/* Social Links */}
            <div className="text-center mb-8">
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                Say hi on social — or just keep reading the roasts 😏
              </p>
              <div className="flex items-center justify-center gap-4">
                <a 
                  href="https://twitter.com/dailywit_news" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 bg-black text-white px-6 py-3 rounded-full font-bold hover:bg-gray-800 transition-colors"
                >
                  <Twitter className="w-5 h-5" />
                  @dailywit.news
                </a>
                <a 
                  href="https://instagram.com/dailywit.news" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 bg-gradient-to-r from-[#833AB4] via-[#FD1D1D] to-[#F77737] text-white px-6 py-3 rounded-full font-bold hover:opacity-90 transition-opacity"
                >
                  <Instagram className="w-5 h-5" />
                  @dailywit.news
                </a>
              </div>
            </div>

            {/* Sign off */}
            <div className="text-center pt-8 border-t-2 border-gray-200 dark:border-gray-700">
              <p className="text-gray-600 dark:text-gray-400 mb-2">See you in the comments,</p>
              <p className="text-2xl font-bold text-[#FF6B6B]" style={{ fontFamily: 'Merriweather, serif' }}>
                David ✌️
              </p>
            </div>

          </div>
        </div>
      </div>
    </>
  );
};

export default MyStory;
